'use strict'

import axios from 'axios'

import { sentryCaptureError } from '@/common/utils/sentryCaptureError'
import { toast } from '@/common/utils/toast'
import { USER_TOKEN_STORAGE_KEY } from '@/common/utils/users'
import i18n from '@/plugins/i18n'
import { SITE_UP_CHECK_URL } from '@/services/maintenance.service.js'
import store from '@/store/'
import { SET_MAINTENANCE_MODE_ENABLED, SET_RETRY_AT } from '@/store/modules/maintenance/mutation_types'

import { SENTRY_REPORTED_STATUS_CODES } from './sentry'
import {
  LOGOUT_ROUTE_NAME,
  TWO_FACTOR_ROUTE_NAME,
  ROOMS_ROUTE_NAME,
  SWITCH_ACCOUNT_ROUTE_NAME,
} from '../router/routerData'
import {
  getCurrentRouteSafe,
  getRouter,
} from '../router/routerInstance'

// Full config:  https://github.com/axios/axios#request-config
axios.defaults.baseURL = import.meta.env.VUE_APP_API_URL || import.meta.env.baseURL || import.meta.env.apiUrl || ''
// axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
// Back-end needs to distinguish between ajax and non-ajax calls because of V1 front-end
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

axios.defaults.withCredentials = true

axios.interceptors.request.use(
  function (config) {
    const token = localStorage.getItem(USER_TOKEN_STORAGE_KEY)
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
      axios.defaults.headers.common.Authorization = `Bearer ${token}`
    }
    // Do something before request is sent
    if (store.state.maintenance.maintenanceModeEnabled) {
      // Only status is allowed here because it's the call to check the maintenance status
      if (config.url !== SITE_UP_CHECK_URL) {
        return {
          ...config,
          signal: AbortSignal.abort(),
        }
      }
    }
    return config
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error)
  },
)

// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    const renewedToken = response.headers['x-closd-token-renewed']
    if (renewedToken) {
      localStorage.setItem(USER_TOKEN_STORAGE_KEY, renewedToken)
      axios.defaults.headers.common.Authorization = `Bearer ${renewedToken}`
    }
    // Do something with response data
    return response
  },
  function (error) {
    if (error.request) {
      const currentRoute = getCurrentRouteSafe()
        // If we were already on a route that would redirect (end of login, two-factor already shown...),
        // use the redirection instead
        const returnRoute = currentRoute.query.returnRoute
          ? currentRoute.query.returnRoute
          : currentRoute.fullPath
      if ((error.request.status === 401) && (error.response?.data?.error === 'Two factor auth required')) {
        getRouter().push({
          name: TWO_FACTOR_ROUTE_NAME,
          query: { returnRoute, locale: i18n.global.locale.value },
        })
      } else if (error.request.status === 401 && localStorage.getItem(USER_TOKEN_STORAGE_KEY)) {
        const otherSessionStarted = error?.response.data?.errorCode === 'ERR_OTHER_SESSION_STARTED'
        if (currentRoute.name !== LOGOUT_ROUTE_NAME) {
          getRouter().push({
            name: LOGOUT_ROUTE_NAME,
            query: {
              returnRoute,
              isLogOut: !otherSessionStarted,
              otherSessionStarted,
            },
          })
        }
      } else if (error.request.status === 401) {
        getRouter().push({
          name: LOGOUT_ROUTE_NAME,
          query: {
            returnRoute,
          },
        })
      }
      if (error.request.status === 429) {
        toast.error(i18n.global.t('common.rateLimited'))
      }
      // If we're getting the wrong user connected, the switch account page handles it. Otherwise, 403s are handled locally
      if ((error.request.status === 403) &&
        (
          error.response?.data?.errorCode === 'ERR_WRONG_USER_LOGGED_IN' ||
          error.response?.data?.errorCode === 'ERR_WRONG_SIGNER'
        )) {
        getRouter().push({ name: SWITCH_ACCOUNT_ROUTE_NAME , params: { locale: i18n.global.locale.value, customMessage: error.response?.data?.errorCode } })
      }
      const roomIsUnderNdaError =
        error.request.status === 422 &&
        error?.response?.data?.errorCode === 'ERR_ENVELOPE_NDA_REQUIRED'
      if (roomIsUnderNdaError) {
        // Check that the request is for the current room, just in case we are querying another room
        const currentMnemo = getRouter().currentRoute?.value?.params?.mnemo
        if (error.request.responseURL.includes(currentMnemo)) {
          getRouter().push({ name: ROOMS_ROUTE_NAME, query: { mnemoUnderNda: currentMnemo } })
        }
      }

      // Interceptor for maintenance mode
      if (error.request.status === 503) {
        store.commit(`maintenance/${SET_MAINTENANCE_MODE_ENABLED}`, true)
        if (error.response.headers['x-retry-at']) {
          store.commit(`maintenance/${SET_RETRY_AT}`, error.response.headers['x-retry-at'])
        }
      }
      // We consider every server error (5xx) as worth reporting to Sentry
      if (
        error.request.status >= 500 ||
        SENTRY_REPORTED_STATUS_CODES.includes(error.request.status) ||
        // Axios wraps response timeouts in an ECONNABORTED error, so we need to check the message
        error.message.includes('timeout')
      ) {
        sentryCaptureError(error, {
          endpoint: error.config.url,
          response: JSON.stringify(error.response?.data),
        })
      }
    }
    return Promise.reject(error)
  },
)

/**
 * Creates a fetch request with axios-configured headers
 * Useful for streaming endpoints that can't use axios directly
 * @param {string} url - The full URL to fetch
 * @param {object} body - The request body to send
 * @returns {Promise<Response>} The fetch response
 */
export function fetchWithAxiosHeaders (url, body) {
  const headers = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }

  const token = localStorage.getItem(USER_TOKEN_STORAGE_KEY)
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  // Copy axios common headers (including auth tokens, etc.)
  if (axios.defaults.headers.common) {
    for (const [key, value] of Object.entries(axios.defaults.headers.common)) {
      if (typeof value === 'string' && !(key in headers)) {
        headers[key] = value
      }
    }
  }

  const baseUrl = (axios.defaults.baseURL || '').replace(/\/$/, '')
  const normalizedUrl = url.replace(/^\//, '')
  const fullUrl = `${baseUrl}/${normalizedUrl}`

  return fetch(fullUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
  })
}
